/**
 * QuestionCard.tsx — per-kind renderers for the config-driven question flow.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: the flow is driven ENTIRELY by the server's question set — this file
 * renders whatever arrives, by `kind`, and hardcodes NO question content (new
 * clinic / new question set = rows, not code; R28 redaction stays a server
 * decision). Behaviours (confirmed against the live question set):
 *   - single_select → auto-advance on tap.
 *   - multi_select  → exclusive "none" pinned TOP; one tap submits [noneKey];
 *                     ticking anything else clears it; Continue submits picks.
 *   - yes_no_text   → "No" advances; "Yes" reveals a free-text follow-up.
 *   - capture       → pre-filled height/weight confirm (Continue).
 *   - date          → themed DatePicker (typeable field + calendar popover
 *                     with fast month/year jump — no date input LIBRARY, it's
 *                     hand-built; see ui/DatePicker.tsx). Capped at today (a
 *                     date of birth can never be in the future).
 *   - photo         → upload now via the signed grant, or defer to dispatch
 *                     (NON-BLOCKING either way; submits the server option key).
 *   - consent       → the MULTI-option agreement step (q15) shows the
 *                     declarations as a READ list with ONE tick under them,
 *                     "I agree to all of the above", and a Continue that
 *                     waits for it (CTO, 2026-09-24 v2); it still submits
 *                     every key, separately — see {@link ConsentQuestion}
 *                     for why it must never collapse into one blanket flag.
 *                     The ONE-option weight declaration (q14) keeps its tick
 *                     + "I agree — continue", and its prompt is finished with
 *                     the captured weight. The agreement step additionally
 *                     hosts the OPTIONAL "Keeping in touch" marketing block
 *                     AFTER that tick, when the caller passes `marketing` —
 *                     see
 *                     {@link ConsentQuestion} for the PECR rules that keep it
 *                     structurally separate.
 *
 * EVERY KIND shares one `Header`: the eyebrow, the prompt, the help text (split
 * into paragraphs on blank lines) and — on question set v6+ — the "Why we ask"
 * link (WhyWeAsk.tsx). The prior-treatment single-select places Switch & Save
 * beside its answers on desktop (see SingleSelectQuestion).
 *
 * DIRTY (navigation resilience): the kinds that hold input behind an explicit
 * "Continue" — capture, date, multi_select, yes_no_text, consent — report
 * "has the patient started filling this in, ahead of Continue saving it?" up
 * via the optional `onDirtyChange`, so `QuestionFlow` can guard its Back/exit
 * against silently discarding it. `single_select` and `photo` are excluded on
 * purpose: both act on tap (no held, losable input sits behind a Continue).
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { grantPhotoUpload, uploadPhoto } from '../api/journey';
import { WEIGHT_PHOTO_NEVER } from '../photos/weightPhotoCopy';
import { MARKETING_CONSENT_WORDING } from '../marketing/consentWording';
import { MarketingConsentFields } from '../marketing/MarketingConsentFields';
import { useWhatsappOffer, type JourneyMarketingTicks } from '../marketing/journeyConsent';
import type { AnswerValue, QuestionOption, QuestionPublic } from '../api/types';
import {
  Button,
  CheckboxRow,
  Eyebrow,
  Field,
  Help,
  OptionButton,
  TextArea,
  TextInput,
  Title,
} from '../ui/kit';
import { DatePicker, todayIso } from '../ui/DatePicker';
import { IconArrowDown, IconRequestPhotos, IconSafety } from '../ui/icons';
import { IconPoints } from '../ui/IconPoints';
import { KeepCompoundsWhole } from '../ui/KeepCompoundsWhole';
import { LAYER_JOURNEY_JUMP } from '../ui/layers';
import { usePrefersReducedMotion } from '../ui/motion';
import { useBelowTheFold } from './agreeJump';
import { formatPence } from '../lib/format';
import { consentGroups, type ConsentGroup } from './consentGroups';
import { ActionRow, JourneyButton } from './JourneyUi';
import { hasParagraphBreak, textParagraphs } from './paragraphs';
import { cheapestPenceForOptionKey, isPlanPreferenceQuestion, stripPriceCopy, usePlanFormPrices } from './planPrices';
import { SwitchAndSave } from './SwitchAndSave';
import { isPriorTreatmentQuestion } from './switchAndSaveCopy';
import { WhyWeAsk } from './WhyWeAsk';

interface QuestionCardProps {
  question: QuestionPublic;
  positionLabel: string;
  /** The clinic whose live plan prices back the plan-preference question. */
  clinicSlug: string;
  capturePrefill?: { heightCm: number; weightKg: number };
  assessmentId: string;
  busy: boolean;
  onSubmit: (value: AnswerValue, detail?: string) => void;
  /** True while this card holds unsaved input ahead of Continue (see DIRTY above). */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * The OPTIONAL "Keeping in touch" marketing ticks, rendered ONLY on the
   * multi-option consent screen (the q15 agreement step) and consumed by no
   * other kind. Absent — the staff fill-on-behalf form, every spec that does
   * not ask for it — renders no block at all, and makes no read. State lives
   * with the caller (JourneyPage): the choice must survive this card
   * unmounting, because it is recorded only AFTER a successful submit (the
   * server 404s any earlier write — see marketing/journeyConsent.ts).
   */
  marketing?: JourneyMarketingTicks;
}

/** Report `dirty` up on every change — a shared one-liner so each kind's
 * effect stays a single, obviously-correct line. Clears on unmount so a
 * submitted card cannot leave the parent guard stuck dirty across the
 * next question (Continue advances before the parent's currentKey reset). */
function useReportDirty(dirty: boolean, onDirtyChange: ((dirty: boolean) => void) | undefined) {
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);
}

/** Render one question by kind and hand the answer up on submit. */
export function QuestionCard(props: QuestionCardProps) {
  switch (props.question.kind) {
    case 'capture':
      return <CaptureQuestion {...props} />;
    case 'date':
      return <DateQuestion {...props} />;
    case 'single_select':
      return <SingleSelectQuestion {...props} />;
    case 'multi_select':
      return <MultiSelectQuestion {...props} />;
    case 'yes_no_text':
      return <YesNoTextQuestion {...props} />;
    case 'photo':
      return <PhotoQuestion {...props} />;
    case 'consent':
      return <ConsentQuestion {...props} />;
  }
}

/**
 * Eyebrow, prompt, help, and — when the question set carries one — the "Why we
 * ask" link (question set v6, 2026-09-24; see WhyWeAsk.tsx). The link renders on
 * the `helpOverride` path too: the plan-preference question swaps its help text,
 * never its explanation.
 *
 * HELP PARAGRAPHS (CTO, 2026-09-24). Help text holding a BLANK LINE is several
 * statements — q14's two verbatim acknowledgements arrive joined by `\n\n` — and
 * one `<p>` collapsed them into a single run-on paragraph. Each paragraph now
 * gets its own `<p>`. Help with no blank line renders exactly as before: one
 * `<Help>` with the server's string untouched.
 *
 * `titleSuffix` follows the prompt INSIDE the heading, for a prompt that is the
 * opening of a sentence the portal completes — the q14 weight readback (see
 * {@link ConsentQuestion}). Kept from wrapping so a number never parts from its
 * unit.
 *
 * "weight-loss" NEVER SPLITS AT ITS HYPHEN (review, 2026-09-25). The prior-
 * treatment heading broke as "Have you used weight-" / "loss treatment before?"
 * at 375px, 1440x790 and 1440x900: the CTO's "weigh-loss", on a question. The
 * prompt and each help paragraph go through `KeepCompoundsWhole`, which only
 * decides where a line may end. The words, their order and the heading's
 * accessible name are the server's, unchanged (A1: presentation only).
 */
function Header({
  question,
  positionLabel,
  helpOverride,
  titleSuffix,
}: {
  question: QuestionPublic;
  positionLabel: string;
  /** When provided, renders this instead of `question.helpText` (empty string =
   * render no help line). The plan-preference question uses it to show the
   * price-stripped helpText while its live prices render on the options. */
  helpOverride?: string;
  titleSuffix?: string;
}) {
  const help = helpOverride !== undefined ? helpOverride : question.helpText;
  const paragraphs = hasParagraphBreak(help) ? textParagraphs(help) : null;
  return (
    <>
      <Eyebrow>{positionLabel}</Eyebrow>
      <Title>
        <KeepCompoundsWhole text={question.prompt} />
        {titleSuffix ? (
          <>
            {' '}
            <span style={{ whiteSpace: 'nowrap' }}>{titleSuffix}</span>
          </>
        ) : null}
      </Title>
      {paragraphs
        ? paragraphs.map((paragraph, index) => (
            // Paragraph spacing inside the help, the usual 18px after the last.
            <Help key={index} style={index < paragraphs.length - 1 ? { marginBottom: 'var(--gps-s-3)' } : undefined}>
              <KeepCompoundsWhole text={paragraph} />
            </Help>
          ))
        : help
          ? (
            <Help>
              <KeepCompoundsWhole text={help} />
            </Help>
          )
          : null}
      <WhyWeAsk text={question.whyWeAsk} />
    </>
  );
}

function FieldError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px', margin: '4px 0' }}>
      {message}
    </p>
  );
}

/** capture — pre-filled height/weight confirm. */
function CaptureQuestion({ question, positionLabel, capturePrefill, busy, onSubmit, onDirtyChange }: QuestionCardProps) {
  const initialCm = capturePrefill ? String(capturePrefill.heightCm) : '';
  const initialKg = capturePrefill ? String(capturePrefill.weightKg) : '';
  const [cm, setCm] = useState(initialCm);
  const [kg, setKg] = useState(initialKg);
  const [error, setError] = useState<string | null>(null);
  // The pre-filled values are NOT "unsaved input" — only a HAND-EDIT away
  // from the Step-0 prefill counts as dirty (this card auto-submits the
  // prefill on the leading capture path; a patient reaching this SCREEN
  // edited it).
  useReportDirty(cm !== initialCm || kg !== initialKg, onDirtyChange);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const heightCm = Number(cm);
    const weightKg = Number(kg);
    if (!Number.isFinite(heightCm) || heightCm < 100 || heightCm > 272 || !Number.isFinite(weightKg) || weightKg < 30 || weightKg > 500) {
      setError('Please check your height (cm) and weight (kg).');
      return;
    }
    setError(null);
    onSubmit({ heightCm, weightKg });
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Header question={question} positionLabel={positionLabel} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gps-s-3)' }}>
        <Field label="Height (cm)" htmlFor="q-height">
          <TextInput className="gps-journey-control" id="q-height" type="number" inputMode="numeric" placeholder="e.g. 175" value={cm} onChange={(e) => setCm(e.target.value)} />
        </Field>
        <Field label="Weight (kg)" htmlFor="q-weight">
          <TextInput className="gps-journey-control" id="q-weight" type="number" inputMode="numeric" placeholder="e.g. 98" value={kg} onChange={(e) => setKg(e.target.value)} />
        </Field>
      </div>
      <FieldError message={error} />
      <ActionRow>
        <JourneyButton type="submit" variant="primary" disabled={busy} arrow>
          Continue
        </JourneyButton>
      </ActionRow>
    </form>
  );
}

/** date — themed DatePicker (typeable field + calendar popover); server
 * enforces the real age rule, this only stops a future date being picked.
 * `openView="dob"`: the date kind IS the date-of-birth question ("What's your
 * date of birth?"), so an empty calendar opens ~18 years back instead of on
 * today. `max` stays today, untouched — the under-18 `hard_stop` (and its
 * signposting) fires on the server from the answer the patient actually gives,
 * so a recent date must remain pickable. */
function DateQuestion({ question, positionLabel, busy, onSubmit, onDirtyChange }: QuestionCardProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  useReportDirty(value !== '', onDirtyChange);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (!value) {
      setError('Please pick a date.');
      return;
    }
    setError(null);
    onSubmit(value);
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Header question={question} positionLabel={positionLabel} />
      <Field label="Date" htmlFor="q-date">
        <DatePicker id="q-date" value={value} onChange={setValue} max={todayIso()} openView="dob" />
      </Field>
      <FieldError message={error} />
      <ActionRow>
        <JourneyButton type="submit" variant="primary" disabled={busy} arrow>
          Continue
        </JourneyButton>
      </ActionRow>
    </form>
  );
}

/** single_select — tap an option and the answer submits (auto-advance).
 *
 * The plan-preference question additionally shows the LIVE "from £X/month" for
 * each option's delivery form, sourced from prices-service (see planPrices.ts).
 * When no price is available (guest/pre-account, prices unconfigured, or the
 * read failed) the option simply shows no price — never a fake number — and the
 * stored helpText is stripped of any stale `£—/month [PLACEHOLDER]` sentence. */
function SingleSelectQuestion({ question, positionLabel, clinicSlug, busy, onSubmit }: QuestionCardProps) {
  const [picked, setPicked] = useState<string | null>(null);
  const planPreference = isPlanPreferenceQuestion(question);
  const planOptions = usePlanFormPrices(clinicSlug, planPreference);
  const answers = (
    <>
      <Header
        question={question}
        positionLabel={positionLabel}
        helpOverride={planPreference ? stripPriceCopy(question.helpText) ?? '' : undefined}
      />
      {(question.options ?? []).map((option) => {
        const pricePence =
          planPreference && planOptions ? cheapestPenceForOptionKey(option.key, planOptions) : null;
        return (
          <OptionButton
            key={option.key}
            control="radio"
            selected={picked === option.key}
            disabled={busy}
            description={pricePence !== null ? `from ${formatPence(pricePence)}/month` : undefined}
            onClick={() => {
              setPicked(option.key);
              onSubmit(option.key);
            }}
          >
            {option.label}
          </OptionButton>
        );
      })}
    </>
  );

  if (!isPriorTreatmentQuestion(question)) return <div>{answers}</div>;

  // Switch & Save (CTO 2026-09-11) — AFTER the answers in the DOM, so the
  // clinical question is read and answered first however the page is navigated.
  //
  // BESIDE THE ANSWERS ON DESKTOP (CTO, 2026-09-24). This screen auto-advances
  // on the tap, so a panel below seven answers was never seen: it sat under the
  // fold of an ordinary laptop and the tap took the patient away first. At
  // ≥880px `gps-jux-sas-layout` makes this a two-column grid that reaches into
  // the right-hand gutter — the answers column keeps its place and width where
  // the gutter has room, and slides left only as far as a narrower window needs
  // (tokens.css has the geometry). Below 880px it is one column, panel under the
  // answers, exactly as before. A width and a margin only — never a transform:
  // the call-back dialog in this screen's footer is a non-portaled `position:
  // fixed` DialogShell, and a transformed ancestor would re-anchor it.
  return (
    <div className="gps-jux-sas-layout">
      <div className="gps-jux-sas-main">{answers}</div>
      <SwitchAndSave className="gps-jux-sas-aside" />
    </div>
  );
}

/** multi_select — exclusive "none" pinned top (one tap); Continue for the rest. */
function MultiSelectQuestion({ question, positionLabel, busy, onSubmit, onDirtyChange }: QuestionCardProps) {
  const [picked, setPicked] = useState<string[]>([]);
  useReportDirty(picked.length > 0, onDirtyChange);
  const options = question.options ?? [];
  const exclusives = options.filter((o: QuestionOption) => o.exclusive);
  const regulars = options.filter((o: QuestionOption) => !o.exclusive);

  function toggle(key: string) {
    setPicked((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  return (
    <div>
      <Header question={question} positionLabel={positionLabel} />
      {exclusives.map((option) => (
        // The exclusive answer gets a control too (CTO 2026-09-24, premium
        // look), so its label starts at the same x as every ticked row. It is
        // still ONE tap that submits, and it never shows as ticked.
        <OptionButton
          key={option.key}
          control="checkbox"
          exclusive
          selected={false}
          disabled={busy}
          onClick={() => onSubmit([option.key])}
        >
          {option.label}
        </OptionButton>
      ))}
      {exclusives.length > 0 && regulars.length > 0 ? (
        <p style={{ fontSize: '13px', color: 'var(--gps-muted)', margin: '4px 0 10px' }}>
          Or tick any that apply to you:
        </p>
      ) : null}
      {regulars.map((option) => (
        <OptionButton
          key={option.key}
          control="checkbox"
          subtle
          selected={picked.includes(option.key)}
          disabled={busy}
          onClick={() => toggle(option.key)}
        >
          {option.label}
        </OptionButton>
      ))}
      <ActionRow>
        <JourneyButton type="button" variant="primary" disabled={busy || picked.length === 0} onClick={() => onSubmit(picked)} arrow>
          Continue
        </JourneyButton>
      </ActionRow>
    </div>
  );
}

/** yes_no_text — "No" advances immediately; "Yes" reveals the follow-up text. */
function YesNoTextQuestion({ question, positionLabel, busy, onSubmit, onDirtyChange }: QuestionCardProps) {
  const [choice, setChoice] = useState<'yes' | 'no' | null>(null);
  const [detail, setDetail] = useState('');
  // "No" submits immediately (see below) — only the "Yes" + free-text detour
  // holds losable input ahead of Continue.
  useReportDirty(choice === 'yes', onDirtyChange);

  return (
    <div>
      <Header question={question} positionLabel={positionLabel} />
      <OptionButton control="radio" selected={choice === 'yes'} disabled={busy} onClick={() => setChoice('yes')}>
        Yes
      </OptionButton>
      <OptionButton
        control="radio"
        selected={choice === 'no'}
        disabled={busy}
        onClick={() => {
          setChoice('no');
          onSubmit('no');
        }}
      >
        No
      </OptionButton>
      {choice === 'yes' ? (
        <div style={{ marginTop: 'var(--gps-s-2)' }}>
          <Field label="Please tell us more" htmlFor="q-detail">
            <TextArea className="gps-journey-control" id="q-detail" placeholder="Please list them…" value={detail} onChange={(e) => setDetail(e.target.value)} />
          </Field>
          <ActionRow>
            <JourneyButton type="button" variant="primary" disabled={busy} onClick={() => onSubmit('yes', detail.trim() || undefined)} arrow>
              Continue
            </JourneyButton>
          </ActionRow>
        </div>
      ) : null}
    </div>
  );
}

/** photo — upload now (signed grant PUT) or defer to dispatch; never blocking. */
function PhotoQuestion({ question, positionLabel, assessmentId, clinicSlug, busy, onSubmit }: QuestionCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = question.options ?? [];
  const deferKey = options.find((o) => /later|dispatch|skip/i.test(o.key))?.key ?? 'add_later';
  const uploadKey = options.find((o) => /now|upload/i.test(o.key))?.key ?? 'upload_now';

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const grant = await grantPhotoUpload(assessmentId, clinicSlug);
      await uploadPhoto(grant, file);
      onSubmit(uploadKey);
    } catch {
      setError('Sorry — the upload did not work. You can try again, or add it before your order is dispatched.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      <Header question={question} positionLabel={positionLabel} />
      <input
        ref={fileInputRef}
        data-testid="photo-input"
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />
      <Button type="button" variant="primary" disabled={busy || uploading} onClick={() => fileInputRef.current?.click()}>
        {uploading ? 'Uploading…' : 'Upload now'}
      </Button>
      <FieldError message={error} />
      <div style={{ marginTop: 'var(--gps-s-2)' }}>
        <JourneyButton type="button" variant="ghost" disabled={busy || uploading} onClick={() => onSubmit(deferKey)} arrow>
          I&rsquo;ll add this before my order is dispatched
        </JourneyButton>
      </div>
      {/* ⚠️ THIS FOOTNOTE ONCE CONTRADICTED THE QUESTION ABOVE IT. The prompt and
          help text are the SERVER's (clinic-service's q14 seed), and that seed
          asks for the patient AND the reading in one frame — while this line,
          written locally, said "the scale reading only". A patient reading both
          followed the smaller, more specific instruction and sent a close-up of a
          number: evidence of nothing, and nothing downstream rejects it (the
          orders gate is presence-only). It now carries the ONE true constraint,
          from the same shared constant the plan step and the dashboard use. */}
      <p style={{ fontSize: '12.5px', color: 'var(--gps-muted)', marginTop: 'var(--gps-s-2)' }}>
        {WEIGHT_PHOTO_NEVER} Either way, this will not hold up your review.
      </p>
    </div>
  );
}

/**
 * The agreement step's ONE tick (CTO, 2026-09-24 v2: *"place a checkbox towards
 * the button; when you check that, it means they gave their consent … and then
 * they can click on the Continue button"*). It is the footer row of the
 * declarations card, so "above" can only mean the declarations in that card —
 * never the optional "Keeping in touch" block, which sits AFTER it.
 */
export const AGREE_TO_ALL_LABEL = 'I agree to all of the above';

/** The agreement step's forward control, enabled only once the tick is ticked. */
export const CONSENT_CONTINUE_LABEL = 'Continue';

/**
 * consent — the agreement step (q15, several declarations) and the q14 weight
 * declaration (ONE box). Two different screens that share a `kind`, so they are
 * two components: {@link AgreementStep} and {@link SingleDeclaration}.
 *
 * THE PER-ITEM RECORD IS THE POINT. Every option key is submitted separately:
 * this screen exists to produce the evidence a clinician relies on at approval
 * — WHICH declarations this patient made — and a single blanket "agreed to
 * everything" flag would destroy the only artefact the screen is for. That
 * argument (2026-08-19) still holds and is why nothing below stores a bulk
 * flag: the submitted answer is `options.map(o => o.key)`, one key per
 * declaration, in the server's option order, byte-identical to every earlier
 * version of this screen.
 *
 * q14 — ONE BOX — IS UNCHANGED: a single declaration the patient ticks, then
 * "I agree — continue", disabled until it is ticked. This is the patient's
 * certification of their own weight, so it keeps the explicit tick.
 *
 * THE q14 WEIGHT READBACK (live defect, fixed 2026-09-24). The declaration's
 * prompt is the OPENING of a sentence — "My current weight is" — which
 * clinic-service's seed expects the portal to finish with the weight captured
 * at q0/q1 (`declaration.weightReadback: true`: one weight, captured once, read
 * back for confirmation — the number the BMI gate ran on and the number the
 * patient certifies cannot disagree). For a consent question with exactly ONE
 * option (the estate's q14 detection rule — every other consent in every set
 * has several), the heading finishes the sentence from
 * `capturePrefill.weightKg`, written the way clinic-service's review renders the
 * same answer to the prescriber (`${weightKg} kg`). Unknown weight (no capture:
 * a 0/0 prefill) → the prompt exactly as served; never a guessed or zero
 * weight. The answer is untouched.
 */
function ConsentQuestion(props: QuestionCardProps) {
  // The q15 agreement step is the consent question with MULTIPLE options (q14
  // carries one). The read list, the one tick and the marketing block all key
  // on it.
  return (props.question.options ?? []).length > 1 ? <AgreementStep {...props} /> : <SingleDeclaration {...props} />;
}

/** q14: one declaration, its own tick, "I agree — continue" gated on it. */
function SingleDeclaration({ question, positionLabel, capturePrefill, busy, onSubmit, onDirtyChange }: QuestionCardProps) {
  const options = question.options ?? [];
  const [ticked, setTicked] = useState<string[]>([]);
  useReportDirty(ticked.length > 0, onDirtyChange);
  const allTicked = options.length > 0 && ticked.length === options.length;
  const readbackWeight = options.length === 1 ? weightReadback(capturePrefill) : null;

  function toggle(key: string) {
    setTicked((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  // THE ACKNOWLEDGEMENTS AS A SHORT ICON LIST (2026-09-26, Ashley
  // Haveloch-Jones via the CTO: "too much text", on a phone). The words are
  // clinic-service's, VERBATIM — the clinical lead's wording, pinned in its seed
  // spec, and the record of what the patient was told before they ticked — so
  // only the LAYOUT changed: each paragraph is one quiet line led by a
  // decorative icon, instead of two full-size grey paragraphs. Shortening the
  // words is a new wording version for the clinical lead, never an edit here.
  const help = question.helpText ?? '';
  const notes = help.trim() === '' ? [] : hasParagraphBreak(help) ? textParagraphs(help) : [help];

  return (
    <div>
      <Header question={question} positionLabel={positionLabel} titleSuffix={readbackWeight ?? undefined} helpOverride={notes.length > 0 ? '' : undefined} />
      {notes.length > 0 ? (
        <IconPoints
          compact
          style={{ margin: '4px 0 var(--gps-s-4)' }}
          points={notes.map((note, index) => ({
            key: String(index),
            // The first acknowledgement is about being asked for evidence (a
            // photo); the rest about the treatment being reviewed. Decorative
            // either way — the words carry every meaning.
            icon: index === 0 ? <IconRequestPhotos /> : <IconSafety />,
            title: <KeepCompoundsWhole text={note} />,
          }))}
        />
      ) : null}
      {options.map((option) => (
        <CheckboxRow
          key={option.key}
          label={option.label}
          checked={ticked.includes(option.key)}
          disabled={busy}
          onChange={() => toggle(option.key)}
        />
      ))}
      <ActionRow>
        <JourneyButton type="button" variant="primary" disabled={busy || !allTicked} onClick={() => onSubmit(options.map((o) => o.key))} arrow>
          I agree — continue
        </JourneyButton>
      </ActionRow>
    </div>
  );
}

/**
 * The q15 agreement step (CTO, 2026-09-24 v2 — SUPERSEDES the same day's
 * one-click "Agree to all and continue" button, which itself replaced the
 * 2026-08-19 "Select all" checkbox). *"We shouldn't have checkboxes if you have
 * the Agree All and Continue buttons. Place a checkbox towards the button; when
 * you check that, it means they gave their consent."*
 *
 * THE DECLARATIONS ARE A READ LIST. No per-item checkbox and no per-item tick
 * or check icon — a row of ticks drawn before the patient has agreed to
 * anything reads as "already agreed". Each declaration is a list item with a
 * neutral bullet, rendered VERBATIM: the wording is clinician-pinned in
 * clinic-service's seed, and shortening or rewording it is a clinical change
 * that needs the clinical lead, not an editor.
 *
 * ONE TICK, IMMEDIATELY AFTER THE DECLARATIONS IT COVERS. "I agree to all of
 * the above" is the FOOTER ROW of the declarations card — inside the card that
 * holds them, ruled off under the last group, and full width under both
 * columns, so "above" can only mean the declarations. Ticking it IS the
 * agreement; Continue is enabled only once it is ticked (until then a VISIBLE
 * line under it quotes the tick's label — never a screen-reader-only hint,
 * see the ActionRow below), and submits every option key, once, in the
 * server's order.
 *   - It never submits twice: Continue is disabled while a submit is in flight
 *     AND an in-flight ref refuses a second press that lands before the
 *     disabling render. The ref is released only when the parent's `busy`
 *     falls again (a failed save leaves the card mounted, and the patient must
 *     be able to press again).
 *   - Nothing is scheduled: there is no timer and no beat any more (the tick
 *     IS the agreement, so there is nothing left to "show landing"), so an
 *     unmount can never post an answer for a screen the patient left.
 *
 * CATEGORIES (question set v6). When `optionGroups` covers every option key
 * exactly once, the declarations sit under their headings (see
 * consentGroups.ts); anything else → the flat list. Either way EVERY
 * declaration is on screen: the tick agrees to every key, so a declaration
 * that vanished would be agreed to unseen. The headings are presentation:
 * never a control, never in the answer.
 *
 * THE "KEEPING IN TOUCH" MARKETING BLOCK (2026-09-23, CTO — the one-flow move
 * from the Result screen). Rendered only when the caller passes `marketing`,
 * and its separation from the required bundle is LEGAL STRUCTURE, not styling
 * (PECR reg. 22 / UK GDPR Art 7 — bundled marketing consent is invalid):
 *   - ITS TICKS LIVE IN SEPARATE STATE (the caller's `MarketingChoices`), never
 *     in `agreed` — so the agreement tick structurally CANNOT reach them, and
 *     `onSubmit(options.map(...))` structurally cannot carry their keys. The
 *     server would reject them anyway (`unknown_consent_key`); the point is
 *     that no edit to this screen's submit can leak them.
 *   - UNTICKED by default, OPTIONAL, and NEVER GATING: Continue waits on the
 *     agreement tick alone; nothing is unlocked by, or waits for, a marketing
 *     tick.
 *   - It sits AFTER the agreement tick and BEFORE Continue, in its own tinted
 *     card with its own heading, outside the declarations card — so the
 *     agreement tick's "all of the above" is read before the block exists on
 *     the page, and cannot be read as covering it. The versioned wording says
 *     it is optional in its first sentence.
 *   - Its one server read (the WhatsApp offer check, see
 *     {@link KeepingInTouchBlock}) gates NOTHING on this screen.
 *   - ONE SELECT-ALL PER SECTION (CTO, 2026-09-27). The block has its own
 *     "Yes to both (email and WhatsApp)" checkbox beside or over its two boxes
 *     (see {@link KeepingInTouchBlock}). It is handed the marketing handle
 *     alone, so it cannot reach `agreed`, and this step's tick cannot reach it.
 *     ONLY THIS STEP'S TICK SAYS "agree to all": on a phone it can be scrolled
 *     under the header while the marketing select-all sits just above the
 *     disabled Continue, so a marketing control using the same words could be
 *     taken for the one that unlocks Continue (not freely given, PECR reg. 22 /
 *     UK GDPR Art 7).
 *
 * ONE WIDE CONTAINER. At ≥880px `.gps-jux-consent-step` widens the whole step —
 * eyebrow, prompt, help, declarations card, tick, "Keeping in touch" and
 * Continue — to min(72rem, 100vw − 4rem), centred on the question column by
 * width + a negative margin (never a transform: the call-back dialog below is a
 * non-portaled `position: fixed` DialogShell, and the Result bar is fixed too),
 * so every edge on the screen lines up and the optional block gets the full
 * width for its two boxes side by side. Continue keeps the column's 40rem
 * width and is CENTRED in the container, with the visible "why it is waiting"
 * line centred UNDER it (CTO, 2026-09-25: on the left edge with the line
 * beside it, the ticked screen left a half-width button stranded on the left,
 * which "looks a bit odd"). Under, not beside, so the button never moves
 * sideways when the line goes on the tick, and the row keeps the line's room
 * once it has gone, so nothing moves up or down either (a page that shrank on
 * the tick dropped everything on screen for a patient scrolled to its end).
 * Below 880px it is the ordinary
 * column in DOM order, and the journey's sticky ActionRow still holds
 * Continue with that line under it. tokens.css has the geometry and the
 * measured heights.
 *
 * THE PHONE'S JUMP TO THE TICK (2026-09-26, the CTO: fewer presses, and a
 * mobile shortcut). Below 880px, while the agreement tick is below the visible
 * page (`journey/agreeJump.ts` says exactly when), a small round arrow floats
 * bottom-right, above the sticky bar: "Go to the agreement tick". One press
 * scrolls the tick's row to the middle of the screen (instantly under reduced
 * motion) and puts focus on the tick; the arrow goes as soon as the tick is on
 * screen — or, while the arrow itself has focus, when focus leaves it (a focused
 * control is never pulled out from under the keyboard). It changes NOTHING about
 * the agreement: it ticks nothing, it is not a select-all, and Continue still
 * waits for the patient's own tick.
 *   - It is first in the DOM after the heading, like a skip link: a
 *     screen-reader user swiping through a long list meets it before the list.
 *   - `position: fixed` on the button itself; no wrapper of journey content is
 *     transformed for it (journeyLayout.spec). Its layer is `LAYER_JOURNEY_JUMP`
 *     (ui/layers.ts): above the page and the sticky row, below the consent
 *     scrim, the R31 ribbon and every dialog. It sits 9rem up — the q15 bar's
 *     own reserve — so it is never over the bar's button.
 *   - No `IntersectionObserver`, no arrow: a control that cannot tell when to
 *     go away would sit over the page for good.
 */
function AgreementStep({
  question,
  positionLabel,
  assessmentId,
  clinicSlug,
  busy,
  onSubmit,
  onDirtyChange,
  marketing,
}: QuestionCardProps) {
  const options = question.options ?? [];
  const groups = consentGroups(question);
  const [agreed, setAgreed] = useState(false);
  useReportDirty(agreed, onDirtyChange);

  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  // Release the in-flight guard when the parent's save FINISHES (busy falls):
  // a failed save leaves this card mounted with an error above it, and the
  // patient must be able to press Continue again.
  const wasBusy = useRef(busy);
  useEffect(() => {
    if (wasBusy.current && !busy) {
      sendingRef.current = false;
      setSending(false);
    }
    wasBusy.current = busy;
  }, [busy]);

  const agreeId = useId();
  const scopeId = useId();
  const hintId = useId();

  /* The tick's ROW (the 44px target and its label) is what the arrow watches and
     scrolls to; the box inside it is what receives focus. A callback ref, so the
     observer attaches when the row actually exists. */
  const [agreeRow, setAgreeRow] = useState<HTMLDivElement | null>(null);
  const agreeBoxRef = useRef<HTMLInputElement | null>(null);
  const tickBelowFold = useBelowTheFold(agreeRow);
  /* ⚠️ A FOCUSED ARROW IS NEVER UNMOUNTED (review, 2026-09-26, WCAG 2.4.3). A
     keyboard user who reaches the arrow and scrolls with the keys (focus stays
     on the button) brings the tick on screen — and the arrow used to go with
     the keyboard still on it, dropping focus to <body> (measured in Chrome at
     375×812 and at 720×450, a laptop at 200% zoom). So it stays while it has
     focus and goes when focus leaves it — a control is never pulled out from
     under the keyboard (the Keeping in touch select-all never hides on a
     press either). Pressing it moves focus to the tick, so a press still
     clears it at once. */
  const [jumpFocused, setJumpFocused] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  function jumpToTick() {
    const box = agreeBoxRef.current;
    if (!agreeRow || !box) return;
    // Focus without the browser's own instant jump, then bring the row to the
    // middle of the screen — clear of the sticky bar at the bottom and the
    // sticky chrome at the top — smoothly unless reduced motion is asked for.
    box.focus({ preventScroll: true });
    agreeRow.scrollIntoView?.({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
  }

  function agreeAndContinue() {
    if (busy || !agreed || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    onSubmit(options.map((o) => o.key));
  }

  const item = (option: QuestionOption) => (
    <li key={option.key} className="gps-jux-consent-item">
      {option.label}
    </li>
  );
  // `role="list"` restores the list semantics Safari/VoiceOver drops from a
  // `list-style: none` list — the count ("list, 3 items") is part of how a
  // screen-reader user learns what they are agreeing to.
  const list = (members: QuestionOption[]) => (
    <ul role="list" className="gps-jux-consent-list">
      {members.map(item)}
    </ul>
  );

  return (
    <div className="gps-jux-consent-step">
      <Header question={question} positionLabel={positionLabel} />
      {tickBelowFold || jumpFocused ? <AgreeJumpButton onPress={jumpToTick} onFocusChange={setJumpFocused} /> : null}
      <div className="gps-jux-consent-card">
        {groups ? <ConsentGroups groups={groups} renderList={list} /> : <div className="gps-jux-consent-flat">{list(options)}</div>}
        {/* The ONE tick — the footer of the card that holds what it covers. */}
        <div className="gps-jux-consent-agree" ref={setAgreeRow}>
          <label htmlFor={agreeId} className="gps-jux-consent-agree-label">
            <input
              id={agreeId}
              ref={agreeBoxRef}
              type="checkbox"
              className="gps-jux-consent-agree-box"
              checked={agreed}
              disabled={busy}
              aria-describedby={scopeId}
              onChange={() => setAgreed((prev) => !prev)}
            />
            <span>{AGREE_TO_ALL_LABEL}</span>
          </label>
          <span id={scopeId} className="gps-sr-only">
            {`This covers the ${options.length} declarations listed above.`}
          </span>
        </div>
      </div>
      {/* After the tick, before Continue — optional, separate, never in the
          answer. See the docblock above. */}
      {marketing ? (
        <KeepingInTouchBlock marketing={marketing} busy={busy} assessmentId={assessmentId} clinicSlug={clinicSlug} />
      ) : null}
      <ActionRow>
        {/* WHY CONTINUE IS WAITING, IN VISIBLE TEXT (WCAG 1.4.1 / 3.3.1 — the
            same rule as the plan step's `blockedReasonLine`): a disabled
            button's only visual change is its opacity, and the two boxes
            nearest to it are the OPTIONAL marketing ones, so without this line
            the obvious guess is that ticking a marketing box unlocks Continue —
            steering patients into the consent PECR reg. 22 needs to be freely
            given. The line quotes the agreement tick's own label, so it can only
            mean that box. It is centred under the button at every width: under
            the centred Continue on desktop, and in the sticky bar on a phone,
            which is exactly where the pinned, disabled button needs its reason.
            The line stays AFTER the button in the DOM (reading order). It is
            plain text, never a live region (one role=alert per surface), and it
            goes the moment the tick is ticked. */}
        <div className="gps-jux-consent-continue">
          <JourneyButton
            type="button"
            variant="primary"
            disabled={busy || !agreed || sending}
            aria-describedby={agreed ? undefined : hintId}
            onClick={agreeAndContinue}
            arrow
          >
            {CONSENT_CONTINUE_LABEL}
          </JourneyButton>
          {agreed ? null : (
            <p id={hintId} className="gps-jux-consent-continue-hint">
              {`Tick "${AGREE_TO_ALL_LABEL}" to continue.`}
            </p>
          )}
        </div>
      </ActionRow>
    </div>
  );
}

/**
 * The Keeping in touch select-all's label — see `KeepingInTouchSelectAll`.
 * EXACTLY these words, at every width (CTO, 2026-09-27, second decision; the
 * first cut said "Agree to all (email and WhatsApp)").
 *
 * ⚠️ IT MAY NEVER SAY "AGREE TO ALL". Only the REQUIRED tick may. On a phone,
 * scrolled to Keeping in touch, "I agree to all of the above" is under the
 * sticky header and the jump arrow is not shown (it shows only while the tick
 * is BELOW the screen), so this box sits alone just above the greyed-out
 * Continue. A marketing tick that also said "agree to all" there could be
 * taken for the one that unlocks Continue — a marketing consent given to get
 * past a gate is not freely given (PECR reg. 22; UK GDPR Art 7(4)), and the
 * ICO names that kind of confusion a dark pattern. "Yes to both" answers the
 * block's own question ("Would you like … to keep in touch …?") and names the
 * two channels, so it can only mean the two boxes beside it.
 *
 * A CONTROL label, not consent wording: what is consented to is each channel
 * box's own versioned words (`mc-2026-09-11-d1`, unchanged), and this line only
 * ticks or unticks those boxes, so it lives here and never in consentWording.
 */
export const KEEP_IN_TOUCH_SELECT_ALL_LABEL = 'Yes to both (email and WhatsApp)';

/** The arrow's accessible name — the button shows only the glyph. */
export const AGREE_JUMP_LABEL = 'Go to the agreement tick';

/**
 * The floating round arrow (see "THE PHONE'S JUMP TO THE TICK" above). A real
 * `<button>` with a 48px target (WCAG 2.5.5), named in words because its face is
 * only a glyph; the glyph is `aria-hidden`. Position, size and colour live in
 * tokens.css (`.gps-jux-agree-jump`, "journey-ux: consent step"); the layer is
 * set here from `ui/layers.ts` so the stacking contract is one number in one
 * module.
 */
function AgreeJumpButton({ onPress, onFocusChange }: { onPress: () => void; onFocusChange: (focused: boolean) => void }) {
  return (
    <button
      type="button"
      className="gps-jux-agree-jump"
      aria-label={AGREE_JUMP_LABEL}
      onClick={onPress}
      onFocus={() => onFocusChange(true)}
      onBlur={() => onFocusChange(false)}
      style={{ zIndex: LAYER_JOURNEY_JUMP }}
    >
      <IconArrowDown size={22} />
    </button>
  );
}

/**
 * The captured weight, worded for the q14 readback — or null when it is not
 * known. `${weightKg} kg` is exactly how clinic-service's review renders the
 * capture answer to the prescriber (`review.ts`, `case 'capture'`), so the
 * sentence the patient certifies and the line the clinician reads name the
 * same number the same way. A missing, zero, negative or non-finite weight is
 * UNKNOWN: the 0/0 prefill is the "no Step-0 capture" marker (QuestionFlow),
 * and a readback of "0 kg" would put a false number in the patient's mouth.
 */
function weightReadback(capturePrefill: QuestionCardProps['capturePrefill']): string | null {
  const kg = capturePrefill?.weightKg;
  if (typeof kg !== 'number' || !Number.isFinite(kg) || kg <= 0) return null;
  return `${kg} kg`;
}

/**
 * How much horizontal room a group's column gets on desktop, relative to its
 * neighbours: its characters, plus an allowance per declaration for the ragged
 * end of its last line and the gap under it. The wording is the server's and
 * its lengths differ a lot — one group holds a 370-character undertaking,
 * another three one-liners — so equal columns leave the longest group towering
 * over the others and push everything below the fold. Weighting the width by
 * the text evens out the HEIGHTS instead. Layout only: it sizes a column and
 * decides nothing about what is shown or agreed to.
 */
export function consentGroupWeight(group: ConsentGroup): number {
  return group.options.reduce((sum, option) => sum + option.label.length + 40, 0);
}

/**
 * The agreement step's declarations under their v6 headings. Each group is a
 * real `role="group"` named by its heading, so a screen reader announces
 * "About you, group" before the list in it. The heading is text, never a
 * control: it cannot be ticked and it is not in the answer.
 *
 * On desktop (≥880px) each group is a column of its own, side by side, its
 * width weighted by {@link consentGroupWeight} (the inline `flex-grow`; it does
 * nothing below 880px, where the groups stack). The DOM order is the reading and
 * tab order at every width.
 */
function ConsentGroups({
  groups,
  renderList,
}: {
  groups: ConsentGroup[];
  renderList: (members: QuestionOption[]) => ReactNode;
}) {
  const baseId = useId();
  return (
    <div className="gps-jux-consent-groups">
      {groups.map((group, index) => {
        const headingId = `${baseId}-g${index}`;
        return (
          <div
            key={headingId}
            role="group"
            aria-labelledby={headingId}
            className="gps-jux-consent-group"
            style={{ flexGrow: consentGroupWeight(group) }}
          >
            <h2 id={headingId} className="gps-jux-consent-group-heading">
              {group.heading}
            </h2>
            {renderList(group.options)}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The optional "Keeping in touch" marketing block on the agreement step — the
 * same versioned wording and boxes as everywhere else (`MarketingConsentFields`
 * renders them; forking the words would detach them from the version id stored
 * with every grant). Visually a separate tinted card with its own heading so it
 * cannot read as part of the required bundle above it. At ≥880px it spans the
 * step's full width with its boxes side by side — and, when both are shown,
 * the select-all as a column of its own before them (`gps-jux-keep-in-touch-fields`,
 * tokens.css) — the words and their order are untouched.
 *
 * THE WHATSAPP BOX IS OFFERED ONLY WHEN THE SERVER SAYS THE LEAD HOLDS A UK
 * MOBILE. The approved words say "on the mobile number I gave you", and the
 * UK-mobile rule lives server-side, so this block reads the assessment's
 * marketing-consent state ONCE on mount (`useWhatsappOffer` — clinic-service
 * answers it for a draft since `fix/consent-step-source`) and adds the box on
 * `whatsappAvailable === true` alone. Loading, failed, 404 (an older
 * clinic-service: the deploy window), hung or late → EMAIL alone, no error, no
 * busy state; the email box and Continue never wait on it.
 *
 * Both boxes render the caller's `choices` — never the stored state the read
 * also carries (a pre-ticked box is not consent, PECR reg. 22).
 *
 * THE BLOCK REPORTS WHAT IT RENDERED. `onWhatsappShownChange` hears `false` on
 * mount and `true` only AFTER the WhatsApp box has been committed to the DOM
 * (an effect, not the render), so the post-submit grant can name WhatsApp only
 * for a box the patient was actually shown. It deliberately reports nothing on
 * unmount: the block unmounts the instant the answer is submitted (Continue),
 * BEFORE the submit resolves and the grant is built, and a reset there would
 * drop the very tick the patient just made. The next mount's `false` is the
 * reset.
 *
 * "YES TO BOTH (EMAIL AND WHATSAPP)" — ONE SELECT-ALL FOR THIS BLOCK ONLY
 * (CTO, 2026-09-27, answering the tick-both sign-off: *"two agree to all
 * buttons to control two sections"*). The screen has two sections and each has
 * its own select-all: the REQUIRED declarations keep their ONE "I agree to all
 * of the above" tick (unchanged; it gates Continue), and this OPTIONAL block
 * gets a real checkbox, `KeepingInTouchSelectAll`, that replaces the
 * 2026-09-26 "Tick both" / "Untick both" button. Its label is NOT an "agree to
 * all" (the CTO's second decision the same day — `KEEP_IN_TOUCH_SELECT_ALL_LABEL`
 * has the reason): only the required tick may say that.
 *   - ⚠️ THE TWO SECTIONS NEVER REACH EACH OTHER. Marketing consent bundled with
 *     the REQUIRED declarations is not freely given, so it is invalid (PECR
 *     reg. 22; UK GDPR Art 7(2) and 7(4)), and the separation on this screen is
 *     STRUCTURAL (CLAUDE.md "MARKETING CONSENT"): this block is handed only the
 *     caller's `marketing` handle, never `agreed` or its setter, so the
 *     select-all cannot tick the agreement, the agreement cannot tick the
 *     select-all or a channel, and Continue never waits on either.
 *   - It comes FIRST in the DOM (`MarketingConsentFields`' `beforeBoxes` slot),
 *     after the intro and before the two channel boxes, so reading and tab
 *     order are select-all → email → WhatsApp at every width. Where it SITS is
 *     tokens.css's alone: below 880px on a line of its own with the two boxes
 *     indented under it (the CTO's phone preview); at ≥880px the first of three
 *     columns on ONE row with the two boxes (CTO, 2026-09-27, second decision),
 *     which is what keeps Continue above a 1440×900 fold. Grid placement only —
 *     the DOM is the same at both widths. Its label names both channels.
 *   - TRI-STATE, DERIVED, NEVER STORED. Ticked when both boxes are ticked,
 *     unticked when neither is, MIXED (`input.indeterminate`, announced as
 *     "mixed") when one is. Ticking it ticks both; unticking it unticks both;
 *     from mixed a press ticks both (the native checkbox's own next state).
 *     Each box still ticks and unticks on its own. It works only through the
 *     caller's per-channel `onToggle`, so the state stays one boolean per
 *     channel and there is no third consent to record.
 *   - ONLY WHEN BOTH BOXES ARE SHOWN. With the email box alone (no UK mobile,
 *     or the offer check has not answered) there is nothing to select "all" of,
 *     and a lone select-all over one box would be a second box for the same
 *     consent. It never hides on a press — it is a checkbox, so a press only
 *     changes its state — and withdrawing is as easy as giving (Art 7(3)).
 *   - Nothing is pre-ticked, and the grant it leads to is unchanged: grants
 *     only, only for boxes SHOWN and TICKED, only `true`, posted once after a
 *     successful submit by `postJourneyMarketingGrant`, under the same wording
 *     version. `journey/consentOneFlow.spec.tsx` (e) pins all of it.
 */
function KeepingInTouchBlock({
  marketing,
  busy,
  assessmentId,
  clinicSlug,
}: {
  marketing: JourneyMarketingTicks;
  busy: boolean;
  assessmentId: string;
  clinicSlug: string;
}) {
  const offerWhatsapp = useWhatsappOffer(assessmentId, clinicSlug);
  const { onWhatsappShownChange } = marketing;
  useEffect(() => {
    onWhatsappShownChange(offerWhatsapp);
  }, [offerWhatsapp, onWhatsappShownChange]);
  const headingId = useId();
  return (
    <section className="gps-jux-keep-in-touch">
      {/* A real heading (the screen's prompt is the h1) so the optional block
          is navigable and announced as its OWN section, never as more of the
          required list. */}
      <h2 id={headingId} className="gps-jux-keep-in-touch-heading">
        {MARKETING_CONSENT_WORDING.heading}
      </h2>
      <MarketingConsentFields
        className="gps-jux-keep-in-touch-fields"
        labelledBy={headingId}
        choices={marketing.choices}
        showWhatsapp={offerWhatsapp}
        disabled={busy}
        onToggle={marketing.onToggle}
        beforeBoxes={
          offerWhatsapp ? (
            <KeepingInTouchSelectAll choices={marketing.choices} disabled={busy} onToggle={marketing.onToggle} />
          ) : undefined
        }
      />
    </section>
  );
}

/**
 * "Yes to both (email and WhatsApp)" — the Keeping in touch block's own
 * select-all (see `KeepingInTouchBlock` for why it exists and what it may never
 * reach, and `KEEP_IN_TOUCH_SELECT_ALL_LABEL` for why it never says "agree to
 * all"). A native checkbox in a label, so the whole row is the target (44px,
 * WCAG 2.5.5) and the browser announces its state, "mixed" included.
 *
 * Its state is DERIVED from the two channel ticks on every render — it holds
 * none of its own, so it can never disagree with the boxes it controls:
 *   neither ticked → unticked · one ticked → mixed · both ticked → ticked.
 * `indeterminate` is a DOM property with no attribute, so a layout effect sets
 * it after EVERY render (no dependency list): a click on a mixed box clears the
 * browser's flag before React re-renders, and a render that left it cleared
 * would draw one-of-two as "unticked".
 */
function KeepingInTouchSelectAll({
  choices,
  disabled,
  onToggle,
}: {
  choices: JourneyMarketingTicks['choices'];
  disabled: boolean;
  onToggle: JourneyMarketingTicks['onToggle'];
}) {
  const id = useId();
  const boxRef = useRef<HTMLInputElement | null>(null);
  const all = choices.email && choices.whatsapp;
  const mixed = choices.email !== choices.whatsapp;
  useLayoutEffect(() => {
    if (boxRef.current) boxRef.current.indeterminate = mixed;
  });
  /* Ticking it (from unticked OR mixed) ticks both; unticking it (from ticked)
     unticks both. Only the boxes not already where the press takes them are
     toggled, through the caller's own per-channel `onToggle` — the same path a
     tap on each box takes — so the state stays one boolean per channel. */
  function selectAll() {
    const target = !all;
    for (const channel of ['email', 'whatsapp'] as const) {
      if (choices[channel] !== target) onToggle(channel);
    }
  }
  return (
    <label htmlFor={id} className="gps-jux-keep-in-touch-all">
      <input
        id={id}
        ref={boxRef}
        type="checkbox"
        className="gps-jux-keep-in-touch-all-box"
        checked={all}
        disabled={disabled}
        onChange={selectAll}
      />
      <span>{KEEP_IN_TOUCH_SELECT_ALL_LABEL}</span>
    </label>
  );
}
